import { BuildRentType, LoanType } from '@apply/constants';
import { Attachment, attachmentInitialState } from '../properties/types';
import { IAddress } from '../cross';

const prefix = 'borrower/';

export const RESET = `${prefix}RESET`;
export const UPDATE_LOAN_TYPE = `${prefix}UPDATE_LOAN_TYPE`;
export const UPDATE_CONFIRM_ENTITY_TYPE = `${prefix}UPDATE_CONFIRM_ENTITY_TYPE`;
export const UPDATE_DEFENDANT_IN_LEGAL_ACTIONS = `${prefix}UPDATE_DEFENDANT_IN_LEGAL_ACTIONS`;
export const UPDATE_INVOLVED_IN_JUDGEMENTS = `${prefix}UPDATE_INVOLVED_IN_JUDGEMENTS`;
export const UPDATE_DELINQUENT_ON_DEBTS = `${prefix}UPDATE_DELINQUENT_ON_DEBTS`;
export const UPDATE_DELINQUENT_ON_TAXES = `${prefix}UPDATE_DELINQUENT_ON_TAXES`;
export const UPDATE_FINANCIALLY_WORSE_OFF = `${prefix}UPDATE_FINANCIALLY_WORSE_OFF`;
export const UPDATE_CURRENTLY_QUESTIONS_EXPLANATIONS = `${prefix}UPDATE_CURRENTLY_QUESTIONS_EXPLANATIONS`;
export const UPDATE_FIRST_LOAN_APPLICATION_WITH_BFI = `${prefix}UPDATE_FIRST_LOAN_APPLICATION_WITH_BFI`;
export const UPDATE_HOW_DID_YOU_HEAR = `${prefix}UPDATE_HOW_DID_YOU_HEAR`;
export const UPDATE_USING_PROPERTY_MANAGEMENT = `${prefix}UPDATE_USING_PROPERTY_MANAGEMENT`;
export const UPDATE_PROPERTY_MANAGEMENT_COMPANY_NAME = `${prefix}UPDATE_PROPERTY_MANAGEMENT_COMPANY_NAME`;
export const UPDATE_PROPERTY_MANAGEMENT_COMPANY_WEBSITE = `${prefix}UPDATE_PROPERTY_MANAGEMENT_COMPANY_WEBSITE`;
export const UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_NAME = `${prefix}UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_NAME`;
export const UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_EMAIL = `${prefix}UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_EMAIL`;
export const UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_PHONE = `${prefix}UPDATE_PROPERTY_MANAGEMENT_COMPANY_CONTACT_PHONE`;
export const UPDATE_CONTACT_INFO_SAME_AS_PROPERTY = `${prefix}UPDATE_CONTACT_INFO_SAME_AS_PROPERTY`;
export const UPDATE_PRINCIPAL_NAME = `${prefix}PRINCIPAL`;
export const UPDATE_PRINCIPAL_EMAIL = `${prefix}UPDATE_PRINCIPAL_EMAIL`;
export const UPDATE_PRINCIPAL_SECONDARY_EMAIL = `${prefix}UPDATE_PRINCIPAL_SECONDARY_EMAIL`;
export const UPDATE_PRINCIPAL_PHONE = `${prefix}UPDATE_PRINCIPAL_PHONE`;
export const UPDATE_BORROWER_ID = `${prefix}UPDATE_BORROWER_ID`;
export const UPDATE_ENTITY_ID = `${prefix}UPDATE_ENTITY_ID`;
export const UPDATE_ENTITY_NAME = `${prefix}UPDATE_ENTITY_NAME`;
export const UPDATE_ENTITY_STATE_INCORPORATED = `${prefix}UPDATE_ENTITY_STATE_INCORPORATED`;
export const UPDATE_BORROWER_MASTERNOTEID = `${prefix}UPDATE_BORROWER_MASTERNOTEID`;
export const UPDATE_ENTITY_ADDRESS_STREET1 = `${prefix}UPDATE_ENTITY_ADDRESS_STREET1`;
export const UPDATE_ENTITY_ADDRESS_STREET2 = `${prefix}UPDATE_ENTITY_ADDRESS_STREET2`;
export const UPDATE_ENTITY_ADDRESS_CITY = `${prefix}UPDATE_ENTITY_ADDRESS_CITY`;
export const UPDATE_ENTITY_ADDRESS_STATE = `${prefix}UPDATE_ENTITY_ADDRESS_STATE`;
export const UPDATE_ENTITY_ADDRESS_ZIPCODE = `${prefix}UPDATE_ENTITY_ADDRESS_ZIPCODE`;
export const REPLACE_BORROWER_STATE = `${prefix}REPLACE_BORROWER_STATE`;
export const UPDATE_BORROWER_INFO_FIRSTLOAN = `${prefix}UPDATE_BORROWER_INFO_FIRSTLOAN`;
export const UPDATE_BORROWER_INFO_SHOULDASKLEGAL = `${prefix}UPDATE_BORROWER_INFO_SHOULDASKLEGAL`;
export const UPDATE_IS_DIRTY = `${prefix}UPDATE_IS_DIRTY`;
export const UPDATE_ESIGN = `${prefix}UPDATE_ESIGN`;
export const UPDATE_API_ID = `${prefix}UPDATE_API_ID`;
export const GUEST_ACKNOWLEDGED_NO_PERSONAL_LOANS = `${prefix}GUEST_ACKNOWLEDGED_NO_PERSONAL_LOANS`;
export const UPDATE_MANAGEMENT_AGREEMENT = `${prefix}UPDATE_MANAGEMENT_AGREEMENT`;
export const UPDATE_FINALIZE_THIRD_PARTY_CLAIM = `${prefix}UPDATE_FINALIZE_THIRD_PARTY_CLAIM`;
export const UPDATE_FINALIZE_THIRD_PARTY_NAME = `${prefix}UPDATE_FINALIZE_THIRD_PARTY_NAME`;

export const entityTypes = [
    { id: 0, name: 'Limited Liability Company (LLC)' },
    { id: 1, name: 'Partnership' },
    { id: 2, name: 'S Corporation' },
    { id: 3, name: 'C Corporation' },
    { id: -1, name: 'N/A - held in name of individual' },
] as const;
export type EntityType = (typeof entityTypes)[number];

export const entityTypesConst = {
    LLC: 0,
    Partnership: 1,
    SCorporation: 2,
    CCorporation: 3,
    NotApplicable: -1,
} as const;

export type FullName = {
    first?: string;
    middle?: string;
    last?: string;
};

export type PrincipalInfo = {
    name?: FullName;
    email?: string;
    phone?: string;
    secondaryEmail?: string;
};

export type PropertyManagementCompany = {
    name?: string;
    website?: string;
    contactName?: FullName;
    contactEmail?: string;
    contactPhone?: string;
    managementAgreement?: Attachment;
};

export type CurrentlyQuestions = {
    defendantInLegalActions?: boolean;
    involvedInJudgements?: boolean;
    delinquentOnDebts?: boolean;
    delinquentOnTaxes?: boolean;
    financiallyWorseOff?: boolean;
    explanations?: string;
};

export type Entity = {
    id: number | undefined;
    name: string;
    stateIncorporated: string;
    typeId: number | undefined;
    address: IAddress;
};

export type Esign = {
    typedName: string;
    title: string;
    date: Date | null;
    eSignFonts: {
        acknowledgeFont: string;
        eSignFont: string;
        termSheetFont: string;
        typedName: string;
        title: string;
    };
    acknowledgeFont: string;
    acknowledgeFontName?: string;
    termsheetFont: string;
    termsheetFontName?: string;
    ipAddress: string;
};

export const esignInitialState: Esign = {
    typedName: '',
    title: '',
    date: null,
    eSignFonts: {
        acknowledgeFont: null,
        eSignFont: null,
        termSheetFont: null,
        typedName: null,
        title: null,
    },
    acknowledgeFont: '',
    acknowledgeFontName: '',
    termsheetFont: '',
    termsheetFontName: '',
    ipAddress: '',
};

export type BorrowerState = {
    id: number;
    apiId?: string;
    token?: string;
    isDirty?: boolean;
    loanType?: LoanType;
    buildRentTypeId?: BuildRentType;
    firstLoanApplicationWithBfi?: boolean;
    howDidYouHear?: string;
    currentlyQuestions?: CurrentlyQuestions;
    usingPropertyManagementCompany?: boolean;
    propertyManagementCompany?: PropertyManagementCompany;
    contactInfoSameAsPropertyContact?: boolean;
    principal?: PrincipalInfo;
    masterNoteId?: number | undefined;
    entity: Entity;
    esign: Esign;
    guestAcknowledgedNoPersonalLoans?: boolean;
    finalizeThirdPartyName: string | undefined;
    finalizeThirdPartyClaim: string | undefined;
    isFirstLoan: boolean;
    shouldAskLegalQuestions: boolean;
};

export type AppraisalInvoice = {
    underwritingFee: number;
    invoiceLines: InvoiceLine[];
    paymentIntentClientSecret: string;
};

export type IdAndToken = {
    id: string;
    token: string;
};

export type InvoiceLine = {
    index: number;
    address1: string;
    address2: string;
    structureType: StructureType;
    cost: number;
    underwritingFee: number;
};

export enum StructureType {
    One = 1,
    TwoToFour = 2,
    FiveToNine = 3,
    TenPlus = 4,
}

export const StructureTypeDescriptions = {
    [StructureType.One]: '1 Unit',
    [StructureType.TwoToFour]: '2 to 4 Units',
    [StructureType.FiveToNine]: '5 to 9 Units',
    [StructureType.TenPlus]: '10+ Units',
};
export const fullNameInitialState: FullName = {
    first: '',
    middle: '',
    last: '',
};

export const principalInitialState: PrincipalInfo = {
    name: { ...fullNameInitialState },
    email: '',
    phone: '',
    secondaryEmail: '',
};

export const businessAddressInitialState: IAddress = {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
};

export const entityInitialState: Entity = {
    id: 0,
    name: '',
    stateIncorporated: '',
    typeId: 0,
    address: { ...businessAddressInitialState },
};

export const propertyManagementCompanyInitialState: PropertyManagementCompany = {
    name: '',
    website: '',
    contactEmail: '',
    contactName: { ...fullNameInitialState },
    contactPhone: '',
    managementAgreement: { ...attachmentInitialState },
};

export const currentlyQuestionsInitialState: CurrentlyQuestions = {
    defendantInLegalActions: undefined,
    involvedInJudgements: undefined,
    delinquentOnDebts: undefined,
    delinquentOnTaxes: undefined,
    financiallyWorseOff: undefined,
    explanations: '',
};

export const borrowerInitialState: BorrowerState = {
    id: 0,
    apiId: '',
    isDirty: false,
    loanType: undefined,
    firstLoanApplicationWithBfi: undefined,
    howDidYouHear: '',
    currentlyQuestions: { ...currentlyQuestionsInitialState },
    usingPropertyManagementCompany: undefined,
    propertyManagementCompany: { ...propertyManagementCompanyInitialState },
    contactInfoSameAsPropertyContact: undefined,
    principal: { ...principalInitialState },
    entity: { ...entityInitialState },
    esign: { ...esignInitialState },
    guestAcknowledgedNoPersonalLoans: undefined,
    finalizeThirdPartyName: undefined,
    finalizeThirdPartyClaim: undefined,
    isFirstLoan: false,
    shouldAskLegalQuestions: false,
};

export const isPropertyManagementCompany = (toCheck: unknown): toCheck is PropertyManagementCompany =>
    (toCheck as PropertyManagementCompany).website !== undefined;

export const isPrincipal = (toCheck: unknown): toCheck is PrincipalInfo => (toCheck as PrincipalInfo).name !== undefined;

export const isBusinessAddress = (toCheck: unknown): toCheck is IAddress => (toCheck as IAddress).street1 !== undefined;

export const isFullName = (toCheck: unknown): toCheck is FullName => (toCheck as FullName).first !== undefined;

export const formatFullName = (value: FullName) => {
    if (!value) {
        return '';
    }
    const middle = value.middle ? ` ${value.middle} ` : ' ';
    return `${value.first}${middle}${value.last}`.trim();
};
